import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { DEFAULT_PRINT_SETTINGS, printMmToWorldMetres } from '../core/print/types';
import { SceneHost } from './scene';

/** Vite HMR: dispose the previous instance's gizmo before remounting. */
let disposePrevious: (() => void) | null = null;

/**
 * Movable translucent cube matching the print build volume (`chunkSizeMm`).
 * Click to select and drag with translate arrows.
 */
export class ChunkCube {
  readonly group = new THREE.Group();
  /** Unscaled pivot the gizmo attaches to — mesh scale lives on the child. */
  private readonly pivot = new THREE.Object3D();
  private readonly mesh: THREE.Mesh;
  private readonly edges: THREE.LineSegments;
  private readonly faceMat: THREE.MeshStandardMaterial;
  private readonly edgeMat: THREE.LineBasicMaterial;
  private readonly gizmo: TransformControls;
  private sizeM = printMmToWorldMetres(DEFAULT_PRINT_SETTINGS.chunkSizeMm);
  private selected = false;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();

  constructor(private host: SceneHost) {
    disposePrevious?.();

    this.group.name = 'chunkCube';
    this.pivot.name = 'chunkCubePivot';
    this.pivot.userData = { kind: 'chunkCube' };

    const geo = new THREE.BoxGeometry(1, 1, 1);
    this.faceMat = new THREE.MeshStandardMaterial({
      color: 0x4fc3f7,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      side: THREE.DoubleSide,
      roughness: 0.85,
      metalness: 0.05,
    });
    this.mesh = new THREE.Mesh(geo, this.faceMat);
    this.mesh.userData = { kind: 'chunkCube' };
    this.mesh.renderOrder = 2;

    this.edgeMat = new THREE.LineBasicMaterial({
      color: 0x29b6f6,
      transparent: true,
      opacity: 0.95,
    });
    this.edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), this.edgeMat);
    this.edges.renderOrder = 3;
    this.mesh.add(this.edges);
    this.pivot.add(this.mesh);
    this.group.add(this.pivot);

    this.applySize(this.sizeM);
    // Near the default orbit target, resting on the ground plane.
    this.pivot.position.set(40, this.sizeM / 2, 15);

    this.gizmo = new TransformControls(host.camera, host.renderer.domElement);
    this.gizmo.setSize(1.1);
    this.gizmo.setMode('translate');
    this.gizmo.enabled = false;
    host.scene.add(this.gizmo.getHelper());

    this.gizmo.addEventListener('dragging-changed', (e) => {
      const dragging = (e as { value?: boolean }).value === true;
      host.orbit.enabled = !dragging;
    });

    disposePrevious = () => {
      this.gizmo.enabled = false;
      this.gizmo.detach();
      host.scene.remove(this.gizmo.getHelper());
      this.gizmo.dispose();
      this.mesh.geometry.dispose();
      (this.edges.geometry as THREE.BufferGeometry).dispose();
      this.faceMat.dispose();
      this.edgeMat.dispose();
    };
  }

  get isSelected(): boolean {
    return this.selected;
  }

  isDragging(): boolean {
    return Boolean((this.gizmo as unknown as { dragging?: boolean }).dragging);
  }

  /** True when the pointer is over this cube's translate gizmo (incl. invisible pickers). */
  hitsGizmo(camera: THREE.Camera, clientX: number, clientY: number, canvas: HTMLElement): boolean {
    if (!this.selected || !this.gizmo.enabled) return false;
    const rect = canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, camera);

    const controls = this.gizmo as unknown as {
      _gizmo?: { picker?: Record<string, THREE.Object3D> };
      mode?: string;
    };
    const picker = controls._gizmo?.picker?.[controls.mode ?? 'translate'];
    const target = picker ?? this.gizmo.getHelper();
    const hits = this.raycaster.intersectObject(target, true);
    // Picker roots are often invisible; accept any hit under them.
    return hits.length > 0;
  }

  /** Keep the cube side length in sync with print settings (world metres). */
  setChunkSizeMm(mm: number): void {
    const size = printMmToWorldMetres(mm);
    if (Math.abs(size - this.sizeM) < 1e-9) return;
    const bottom = this.pivot.position.y - this.sizeM / 2;
    this.applySize(size);
    this.pivot.position.y = bottom + size / 2;
  }

  setVisible(on: boolean): void {
    this.group.visible = on;
    if (!on) this.setSelected(false);
  }

  setSelected(on: boolean): void {
    if (this.selected === on) {
      if (on) this.attachGizmo();
      return;
    }
    this.selected = on;
    this.faceMat.opacity = on ? 0.28 : 0.16;
    this.edgeMat.color.setHex(on ? 0xffcc33 : 0x29b6f6);
    if (on) this.attachGizmo();
    else this.detachGizmo();
  }

  /** Ray-pick the cube under the cursor. */
  pickAtScreen(camera: THREE.Camera, clientX: number, clientY: number, canvas: HTMLElement): boolean {
    if (!this.group.visible) return false;
    const rect = canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, camera);
    return this.raycaster.intersectObject(this.mesh, false).length > 0;
  }

  private applySize(sizeM: number): void {
    this.sizeM = sizeM;
    this.mesh.scale.setScalar(sizeM);
  }

  private attachGizmo(): void {
    this.gizmo.attach(this.pivot);
    this.gizmo.enabled = true;
  }

  private detachGizmo(): void {
    this.gizmo.enabled = false;
    this.gizmo.detach();
  }
}
